import { useMemo } from 'react';
import { EventBasedStore, pickStoreActions } from '../utils/EventBasedStore';
import { NL_LAW_ROUTES, showBalanceStorageKey } from '../utils/constants';
import { userWalletStore } from './userWallet';
import { isBrowser } from '../utils/trick';
import { Web3ConnectWalletModalUIProps } from '../components/Web3ConnectWalletModal';

type VisibleState = {
  visible: boolean;
};
type DialogState<T extends Record<string, unknown> = Record<string, unknown>> = VisibleState & T;

type DialogStoreOption<T extends Record<string, unknown> = Record<string, unknown>> = {
  // 在show的setState之前调用，用于改变set之前的状态
  beforeShow?: (state: DialogState<T>) => DialogState<T>;
  //
  beforeHide?: (state: DialogState<T>) => DialogState<T>;
  resetWhenHide?: boolean;
  initializer?: () => void;
};

/**
 * 创建一个 弹窗状态store
 * @param initial 初始状态
 * @returns
 */
const createDialogStore = <T extends Record<string, unknown> = Record<string, unknown>>(
  initial: T & Partial<VisibleState>,
  opt?: DialogStoreOption,
) => {
  class DialogStore extends EventBasedStore<DialogState<T>> {
    show(props?: T) {
      const visible = true;
      const newState = {
        ...props,
        visible,
      } as DialogState<T>;
      // @ts-ignore
      this.setState(opt?.beforeShow ? opt.beforeShow(newState) : newState);
    }
    hide(reset = opt?.resetWhenHide ?? false) {
      if (!reset) {
        this.setState({ visible: false } as DialogState<T>);
        return;
      }
      if (opt?.beforeHide) {
        this.setState({
          ...opt.beforeHide(this.state),
          visible: false,
        } as DialogState<T>);
      } else {
        this.reset();
      }
    }
  }
  const store = new DialogStore(
    {
      visible: false,
      ...initial,
    },
    undefined,
    opt?.initializer,
  );

  const useDialog = () => {
    const state = store.useStoreState();
    const actions = useMemo(() => {
      return pickStoreActions(store, ['show', 'hide']);
    }, []);
    return {
      ...state,
      ...actions,
    };
  };
  return [useDialog, store] as const;
};

/**
 * 连接钱包弹窗
 *
 */
export enum WalletModalTab {
  bybitWallet = 'bybit',
  thirdWallet = 'third',
}

export const [useConnectWalletModal, connectWalletModalStore] = createDialogStore<Web3ConnectWalletModalUIProps>(
  {
    activeTab: WalletModalTab.bybitWallet,
    visible: false,
  },
  {
    beforeShow: (state) => {
      return {
        onConnected: undefined,
        ...state,
      };
    },
  },
);

/**
 * 受限地区弹窗
 * 判断当前路径的业务是否是荷兰受限业务，要自动弹弹窗
 * @returns boolean
 */
export const isNlLawRestrict = () => {
  if (!isBrowser) {
    return false;
  }
  const { hostname, pathname } = window.location;
  if (hostname.endsWith('.nl') && userWalletStore.state.isBrowserWallet) {
    const path = `/${pathname
      .split('/')
      .slice(2)
      .filter((v) => v)
      .join('/')}`;
    return Boolean(NL_LAW_ROUTES.find((v) => path.startsWith(v)));
  }
  return false;
};
export const [useLawRestrictModal, lawRestrictModalStore] = createDialogStore(
  {},
  {
    initializer: () => {
      const visible = isNlLawRestrict();
      if (visible) {
        lawRestrictModalStore.setState({ visible });
      }
      userWalletStore.on('update', () => {
        lawRestrictModalStore.setState({
          visible: isNlLawRestrict(),
        });
      });
    },
  },
);

/** 创建弹窗面板类型 */
export enum WalletCreatePanel {
  Create = 1,
  Pending = 2,
  Success = 3,
  Failed = 4,
}

export const [useCreateWalletModal, createWalletModalStore] = createDialogStore<{
  panel: WalletCreatePanel;
  onCreate?: () => void;
}>(
  {
    panel: WalletCreatePanel.Create,
  },
  {
    beforeShow: (state) => {
      return {
        onCreate: undefined,
        ...state,
      };
    },
  },
);

/**
 * 是否显示余额的切换，虽然不是弹窗，将就复用下这个visible状态
 */
export const [useShowBalance, showBalanceStore] = createDialogStore(
  {
    visible: isBrowser ? localStorage.getItem(showBalanceStorageKey) !== 'false' : false,
  },
  {
    initializer: () => {
      if (isBrowser) {
        showBalanceStore.on('update', ({ visible }) => {
          if (visible) {
            localStorage.removeItem(showBalanceStorageKey);
          } else {
            localStorage.setItem(showBalanceStorageKey, 'false');
          }
        });
      }
    },
  },
);
